/**
 * Dialogues de confirmation — Souffle Diplomatique.
 *
 * Remplace `window.confirm()` (moche, hors-thème, bloquant) par une modale
 * habillée en note de service du Secrétariat : papier crème, en-tête
 * administratif, tampon, deux actions explicites.
 *
 * API : `await confirmDialog({...})` → true si confirmé, false sinon.
 * Échap / clic sur le fond = annuler ; Entrée = confirmer.
 */

export interface ConfirmOptions {
  /** Eyebrow administratif (défaut : COMMUNIQUÉ DU SECRÉTARIAT) */
  eyebrow?: string;
  title: string;
  /** Corps : lignes de texte (chaque entrée = un paragraphe) */
  body: string[];
  /** Libellé du bouton de confirmation */
  confirmLabel: string;
  /** Libellé du bouton d'annulation (défaut : Annuler) */
  cancelLabel?: string;
  /** Tampon décoratif affiché en travers (ex. "SANS EFFET", "BLOQUÉ") */
  stamp?: string;
  /** danger = action destructrice (rouge), default = neutre */
  tone?: "default" | "danger";
}

let openDialog: { resolve: (v: boolean) => void; el: HTMLElement } | null = null;

function esc(s: string): string {
  const d = document.createElement("div");
  d.textContent = s;
  return d.innerHTML;
}

export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  // Un seul dialogue à la fois : le précédent est annulé.
  if (openDialog) {
    openDialog.resolve(false);
    openDialog.el.remove();
    openDialog = null;
  }

  return new Promise<boolean>((resolve) => {
    const el = document.createElement("div");
    el.className = "sd-dialog";
    el.setAttribute("role", "dialog");
    el.setAttribute("aria-modal", "true");
    el.innerHTML = `
      <div class="sd-dialog-backdrop"></div>
      <div class="sd-dialog-panel${opts.tone === "danger" ? " sd-dialog-danger" : ""}">
        <div class="sd-dialog-eyebrow">${esc(opts.eyebrow ?? "Communiqué du Secrétariat")}</div>
        <h2 class="sd-dialog-title">${esc(opts.title)}</h2>
        ${opts.stamp ? `<div class="sd-dialog-stamp">${esc(opts.stamp)}</div>` : ""}
        <div class="sd-dialog-body">
          ${opts.body.map((p) => `<p>${esc(p)}</p>`).join("")}
        </div>
        <div class="sd-dialog-actions">
          <button type="button" class="sd-dialog-btn sd-dialog-cancel">${esc(opts.cancelLabel ?? "Annuler")}</button>
          <button type="button" class="sd-dialog-btn sd-dialog-confirm">${esc(opts.confirmLabel)}</button>
        </div>
      </div>`;
    document.body.appendChild(el);

    const finish = (value: boolean) => {
      if (!openDialog || openDialog.el !== el) return;
      openDialog = null;
      window.removeEventListener("keydown", onKey, true);
      el.classList.remove("sd-dialog-show");
      window.setTimeout(() => el.remove(), 200);
      resolve(value);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        finish(false);
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        finish(true);
      }
    };

    el.querySelector(".sd-dialog-cancel")?.addEventListener("click", () => finish(false));
    el.querySelector(".sd-dialog-confirm")?.addEventListener("click", () => finish(true));
    el.querySelector(".sd-dialog-backdrop")?.addEventListener("click", () => finish(false));
    window.addEventListener("keydown", onKey, true);

    openDialog = { resolve, el };
    // reflow → transition d'entrée
    void el.offsetWidth;
    el.classList.add("sd-dialog-show");
    (el.querySelector(".sd-dialog-confirm") as HTMLElement | null)?.focus();
  });
}

// ============================================================ //
// ÉCRAN D'ATTENTE DE CONNEXION                                  //
// ============================================================ //
//
// Le serveur Fly s'endort quand personne ne joue (auto_stop_machines) : la
// première connexion après une période creuse peut prendre 10 à 30 secondes,
// le temps que la machine redémarre. Sans retour visuel, l'utilisateur croit
// que le site est planté et spamme le bouton. On occupe donc l'attente avec
// une file de messages qui tourne le cold start en gag bureaucratique.

const WAKE_MESSAGES: Array<{ at: number; text: string }> = [
  { at: 0, text: "Transmission de votre accréditation au Secrétariat…" },
  { at: 2500, text: "Les locaux sont fermés. Réveil du concierge en cours." },
  { at: 6000, text: "Le concierge cherche ses clés. Il est confiant." },
  { at: 10000, text: "Le concierge a trouvé un trousseau. Ce n'est pas le bon." },
  { at: 15000, text: "Ouverture des fenêtres — procédure obligatoire avant toute séance." },
  { at: 21000, text: "Dépoussiérage de la table de négociation. Il y avait beaucoup de poussière." },
  { at: 28000, text: "Le Secrétariat présente ses excuses pour ce délai « exceptionnel »." },
  { at: 36000, text: "Un mémo a été rédigé au sujet de ce retard. Il sera classé sans suite." },
];

let connectingEl: HTMLElement | null = null;
let connectingTimers: number[] = [];

function clearConnectingTimers() {
  for (const t of connectingTimers) window.clearTimeout(t);
  connectingTimers = [];
}

/** Affiche l'écran d'attente. `title` décrit l'action en cours. */
export function showConnectingOverlay(title: string): void {
  hideConnectingOverlay();
  const el = document.createElement("div");
  el.className = "sd-connecting";
  el.innerHTML = `
    <div class="sd-connecting-backdrop"></div>
    <div class="sd-connecting-panel">
      <div class="sd-connecting-seal" aria-hidden="true">CM</div>
      <div class="sd-connecting-eyebrow">Conseil Mondial contre la Pet-lution</div>
      <h2 class="sd-connecting-title">${esc(title)}</h2>
      <div class="sd-connecting-bar"><div class="sd-connecting-bar-fill"></div></div>
      <p class="sd-connecting-msg" id="sd-connecting-msg" role="status" aria-live="polite">${esc(WAKE_MESSAGES[0]!.text)}</p>
      <p class="sd-connecting-hint">Le serveur se met en veille quand personne ne joue : le premier réveil prend quelques secondes. Inutile de re-cliquer.</p>
      <div class="sd-connecting-actions" id="sd-connecting-actions" hidden></div>
    </div>`;
  document.body.appendChild(el);
  connectingEl = el;
  void el.offsetWidth;
  el.classList.add("sd-connecting-show");

  for (const m of WAKE_MESSAGES.slice(1)) {
    connectingTimers.push(
      window.setTimeout(() => {
        const p = document.getElementById("sd-connecting-msg");
        if (!p) return;
        p.classList.remove("sd-connecting-msg-in");
        void p.offsetWidth;
        p.textContent = m.text;
        p.classList.add("sd-connecting-msg-in");
      }, m.at)
    );
  }
}

/** Bascule l'écran d'attente en état d'échec, avec un bouton de reprise. */
export function connectingFailed(message: string, onRetry: () => void): void {
  clearConnectingTimers();
  if (!connectingEl) return;
  const panel = connectingEl.querySelector(".sd-connecting-panel");
  panel?.classList.add("sd-connecting-error");
  const title = connectingEl.querySelector(".sd-connecting-title");
  if (title) title.textContent = "La séance n'a pas pu s'ouvrir";
  const msg = document.getElementById("sd-connecting-msg");
  if (msg) msg.textContent = message;
  const bar = connectingEl.querySelector(".sd-connecting-bar") as HTMLElement | null;
  if (bar) bar.style.display = "none";
  const actions = document.getElementById("sd-connecting-actions");
  if (actions) {
    actions.hidden = false;
    actions.innerHTML = `
      <button type="button" class="sd-dialog-btn sd-connecting-close">Fermer</button>
      <button type="button" class="sd-dialog-btn sd-dialog-confirm sd-connecting-retry">Réessayer</button>`;
    actions.querySelector(".sd-connecting-close")?.addEventListener("click", () => hideConnectingOverlay());
    actions.querySelector(".sd-connecting-retry")?.addEventListener("click", () => {
      hideConnectingOverlay();
      onRetry();
    });
  }
}

export function hideConnectingOverlay(): void {
  clearConnectingTimers();
  const el = connectingEl;
  connectingEl = null;
  if (!el) return;
  el.classList.remove("sd-connecting-show");
  window.setTimeout(() => el.remove(), 220);
}
